import { useEffect, useRef, useState } from "react";
import { Box, Calendar, ClipboardList, Package, QrCode, Search, Wrench } from "lucide-react";
import { request } from "./FunctionalApp";
import { QrScanner } from "./QrScanner";

const TYPE_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  ASSET:       { icon: Box,           color: "#0b9f91", label: "Asset" },
  WORK_ORDER:  { icon: Wrench,        color: "#f59e0b", label: "Work order" },
  PART:        { icon: Package,       color: "#3b82f6", label: "Part" },
  TICKET:      { icon: ClipboardList, color: "#8b5cf6", label: "Helpdesk" },
  PPM:         { icon: Calendar,      color: "#10b981", label: "PPM" },
  ORDER:       { icon: Box,           color: "#f97316", label: "Order" },
  APPOINTMENT: { icon: Calendar,      color: "#ec4899", label: "Appointment" },
};

export function GlobalSearch({ onOpen }: { onOpen: (type: string, id: string) => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await request(`/api/search?q=${encodeURIComponent(q)}`);
        if (r.ok) {
          const d = await r.json();
          setResults(d.results || []);
        } else {
          setResults([]);
        }
      } catch { setResults([]); }
      setLoading(false);
    }, 280);
  }, [q]);

  // Group results by type
  const grouped = results.reduce((acc: any, r: any) => {
    if (!acc[r.tag]) acc[r.tag] = [];
    acc[r.tag].push(r);
    return acc;
  }, {});

  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Search</h1>
          <p>Search across assets, work orders, parts, helpdesk tickets, PPMs and more.</p>
        </div>
      </div>
      <section className="func-panel" style={{padding:20}}>
        <div className="gs-input-wrap">
          <Search size={18} className="gs-icon" />
          <input
            ref={inputRef}
            autoFocus
            className="gs-input"
            placeholder="Search assets, work orders, parts, tickets…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <button className="gs-qr-btn" title="Scan QR code" onClick={() => setShowQr(true)}>
            <QrCode size={18} />
          </button>
          {loading && <div className="gs-spinner" />}
        </div>
        {showQr && (
          <QrScanner
            label="Scan QR code to search"
            onScan={val => { setQ(val); setShowQr(false); inputRef.current?.focus(); }}
            onClose={() => setShowQr(false)}
          />
        )}

        {q.length >= 2 && results.length === 0 && !loading && (
          <p className="func-empty" style={{marginTop:24}}>No results found for "<b>{q}</b>" — try a different term, part code, WO number or asset name.</p>
        )}

        {Object.entries(grouped).map(([tag, items]: any) => (
          <div key={tag} className="gs-group">
            <div className="gs-group-label">{tag}</div>
            {items.map((r: any) => {
              const cfg = TYPE_CONFIG[r.type] || { icon: Search, color: "#6b7280", label: r.type };
              const Icon = cfg.icon;
              return (
                <button key={r.id} className="gs-result" onClick={() => onOpen(r.type, r.id)}>
                  <span className="gs-result-icon" style={{background: cfg.color + "18", color: cfg.color}}>
                    <Icon size={16} />
                  </span>
                  <span className="gs-result-text">
                    <b>{r.title}</b>
                    {r.subtitle && <small>{r.subtitle}</small>}
                  </span>
                  <span className="gs-result-tag" style={{color: cfg.color, borderColor: cfg.color + "40"}}>
                    {tag}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </section>
    </>
  );
}
